import React, { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import * as AppConstants from '../../../app/AppConstants';

import { Typography, Box, Button, Grid, IconButton } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import SearchIcon from '@mui/icons-material/Search';
import Refresh from '@mui/icons-material/Refresh';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';

import BackToMenuButton from '../../../components/util/Button/BackToMenuButton';
import ManagePaymentDialog from '../../../components/base/ApplicationDialog/ManagePaymentDialog';
import PayInPersonProcedureDialog from '../../../components/base/ApplicationDialog/PayInPersonProcedureDialog';
import CCPProcedureDialog from '../../../components/base/ApplicationDialog/CCPProcedureDialog';
import ApplicationDetailsDialog from '../../../components/base/ApplicationDialog/ApplicationDetailsDialog';
import PhotoUploadDialog from '../../../components/base/PhotoUpload/PhotoUploadDialog';
import UserInfoPanel, { getAdminOffice, getAdminOfficeCentre, getPaymentCounter, getAdminOfficeUrl, getPaymentCounterUrl } from '../../../components/base/UserInfoPanel/UserInfoPanel'

import slice, { dispatchSearchInput, searchHistory, dispatchPaymentMethodInput, updatePhoto } from './UserHistorySlice';

function UserHistory(props) {
  // handle screen size changed
  const [width, setWidth] = React.useState(window.innerWidth);
  const breakpoint = 600;
  React.useEffect(() => {
    const handleResizeWindow = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResizeWindow);
    return () => { window.removeEventListener("resize", handleResizeWindow); };
  }, []);

  return (
    <>
      <Typography variant="pageTitle">Application History</Typography>

      <Box mb={2} p={2} style={width > breakpoint ? {} : { overflowY: 'hidden' }}>
        <UserInfoPanel />
        <Grid container spacing={2}>
          <Grid item xs={12}><hr /></Grid>
          <Grid item xs={12}>
            <HistoryTable width={width} breakpoint={breakpoint}/>
          </Grid>
        </Grid>
      </Box>

      <br />
      <BackToMenuButton />
    </>
  );
}

function PhotoPreviewButton(row, dispatch) {
  return (
    row?.photo?.data && <IconButton
      onClick={() => dispatch(slice.actions.setPhotoInput({
        applicationId: row?.applicationId,
        allowReupload: row?.allowReupload,
        photoBuf: row?.photo?.data
      }))}
    >
      <ImageSearchIcon />
    </IconButton>
  )
}

function HistoryTable(props) {
  const appState = useSelector((state)=>state.app);
  const dispatch = useDispatch();

  // Slice for this page
  const sliceState = useSelector((state) => state.userHistory);

  // handle screen size changed
  useEffect(() => {
    if (props.width > props.breakpoint && sliceState.selectedRow.details != null) {
      dispatch(slice.actions.setRowDetails(null));
    }
  }, [props.width > props.breakpoint]);

  // Reset slice when page is unloaded
  useEffect(() => {
    dispatch(slice.actions.resetStore());
    dispatch(dispatchSearchInput([
      { name: 'searchType', value: AppConstants.SEARCH_TYPE.SELF },
      { name: 'netId', value: appState.me.netid?.netid },
    ]));
    dispatch(searchHistory());

    return () => dispatch(slice.actions.resetStore());
  }, []);

  const handleManagePaymentClicked = (id, currentPaymentMethod) => {
    dispatch(dispatchPaymentMethodInput({
      paymentMethodInput: [
        { name: 'applicationId', value: id },
        { name: 'paymentMethod', value: currentPaymentMethod },
      ],
      startUpdate: false,
    }));
    dispatch(slice.actions.setIsManagePaymentDialogOpen(true));
    setTimeout(() => {
      dispatch(slice.actions.setRowDetails(null));
    }, 100);
  }

  const handlePaymentMethodClicked = next => {
    dispatch(slice.actions.setIsManagePaymentDialogOpen(false));
    setTimeout(() => {
      dispatch(dispatchPaymentMethodInput({
        paymentMethodInput: [{ name: 'paymentMethod', value: next }],
        startUpdate: true,
      }));
    }, 100);
  }

  const handleUpdatePhotoClicked = photo => {
    // update application photo
    dispatch(updatePhoto({
      applicationId: sliceState.selectedRow.photoInput.applicationId,
      photo: photo
    }));
    setTimeout(() => {
      dispatch(slice.actions.setRowDetails(null));
    }, 100);
  }

  const applicationReasons = (Object).values(AppConstants.APPLICATION_REASON);
  const applicationAdditionals = (Object).values(AppConstants.APPLICATION_ADDITIONAL);
  const applicationPaymentMethods = (Object).values(AppConstants.APPLICATION_PAYMENT_METHOD);

  const columnForDataGrid = [
    {
      field: 'action', headerName: '', headerAlign: "center", sortable: false, align: "center", minWidth: 150, flex: 1,
        renderCell: (params) => {
          return <Button variant='contained'
            disabled={!!params?.row.paidAt || !!params?.row.cancelledAt}
            style={{fontSize: 12, width: 160}}
            onClick={() => handleManagePaymentClicked(params?.row.applicationId, params?.row.paymentMethod)}
          >
            Manage Payment
          </Button>
        }
    },
    { field: 'applicationId', headerName: 'Application ID', headerAlign: "center", sortable: true, align: "center", minWidth: 130, flex: 1, },
    {
      field: 'appliedAt', headerName: 'Application Date', headerAlign: "center", sortable: true, align: "center", minWidth: 140, flex: 1,
        valueFormatter: params => moment(params?.value).format("YYYY-MM-DD HH:mm")
    },
    {
      field: 'applicationChannel', headerName: 'Created By', headerAlign: "center", sortable: true, align: "center", minWidth: 100, flex: 1,
        valueGetter: (params) =>
          params?.value === AppConstants.APPLICATION_CHANNEL.SELF ? "Self"
          : params?.value === AppConstants.APPLICATION_CHANNEL.ADMIN ? "Admin"
          : null
    },
    {
      field: 'reason', headerName: 'Reason', headerAlign: "center", sortable: true, align: "center", minWidth: 110, flex: 1,
        valueGetter: (params) => applicationReasons.find(r => r.ID === params?.value)?.NAME + (params?.row.isTempCard ? "\n(Temp Card)" : ''),
        renderCell: (params) => { return <p style={{whiteSpace:'pre-wrap', textAlign: 'center'}}>{params?.value}</p> }
    },
    {
      field: 'additionalService', headerName: 'Other Service', headerAlign: "center", sortable: true, align: "center", minWidth: 140, flex: 1,
        valueGetter: (params) => applicationAdditionals.find(a => a.ID === params?.value)?.NAME,
        renderCell: (params) => { return <p style={{whiteSpace:'pre-wrap', textAlign: 'center'}}>{params?.value}</p> }
    },
    {
      field: 'photo', headerName: 'Photo', headerAlign: "center", sortable: true, align: "center", minWidth: 140, flex: 1,
        renderCell: (params) => {
          return PhotoPreviewButton(params?.row, dispatch)
        }
    },
    {
      field: 'paymentMethod', headerName: 'Payment Method', headerAlign: "center", sortable: true, align: "center", minWidth: 150, flex: 1,
        valueGetter: (params) => params?.value === AppConstants.APPLICATION_PAYMENT_METHOD.NOPAYMENT.ID && !params?.row.paidAt
          ? AppConstants.APPLICATION_PAYMENT_METHOD.TBC.NAME
          : applicationPaymentMethods.find(p => p.ID === params?.value)?.NAME,
        renderCell: (params) => { return <p style={{whiteSpace:'pre-wrap', textAlign: 'center'}}>{params?.value}</p> }
    },
    {
      field: 'paymentStatus', headerName: 'Payment Status', headerAlign: "center", sortable: true, align: "center", minWidth: 130, flex: 1,
        valueGetter: (params) =>
          params?.row.paidAt ? AppConstants.APPLICATION_STATUS.PAID.NAME : AppConstants.APPLICATION_STATUS.PENDING.NAME
    },
    {
      field: 'paymentRemark', headerName: 'Payment Remark', headerAlign: "center", sortable: true, align: "center", minWidth: 170, flex: 1,
      renderCell: (params) => {
        return <p style={{whiteSpace:'pre-wrap', textAlign: 'left'}}>{params?.value}</p>
      }
    },
    {
      field: 'printStatus', headerName: 'Print Status', headerAlign: "center", sortable: true, align: "center", minWidth: 100, flex: 1,
        valueGetter: (params) =>
          params?.row.printedAt ? AppConstants.APPLICATION_STATUS.PRINTED.NAME
          : params?.row.paidAt ? AppConstants.APPLICATION_STATUS.PENDING.NAME
          : null
    },
    {
      field: 'collectionStatus', headerName: 'Collection Status', headerAlign: "center", sortable: true, align: "center", minWidth: 130, flex: 1,
        renderCell: (params) => {
          return <p style={{whiteSpace:'pre-wrap', textAlign: 'center'}}>
            {
              params?.row.collectedAt ? AppConstants.APPLICATION_STATUS.COLLECTED.NAME
              : params?.row.printedAt && params?.row.lastNotifiedAt ? AppConstants.APPLICATION_STATUS.PENDINGCOLLECT.NAME
              : params?.row.printedAt ? "Not Notified for Collection"
              : null
            }
          </p>
        }
    },
    {
      field: 'cancelStatus', headerName: 'Cancellation Status', headerAlign: "center", sortable: true, align: "center", minWidth: 150, flex: 1,
      valueGetter: (params) =>
        params?.row.cancelledAt ? AppConstants.APPLICATION_STATUS.CANCELLED.NAME : null
    },
  ];

  const columnForDataGridVertical = [
    {
      field: 'appliedAt', headerName: 'Application Date', headerAlign: "center", sortable: true, align: "center", minWidth: 140, flex: 1,
        valueFormatter: params => moment(params?.value).format("YYYY-MM-DD HH:mm")
    },
    {
      field: 'reason', headerName: 'Reason', headerAlign: "center", sortable: true, align: "center", minWidth: 110, flex: 1,
        valueGetter: (params) => applicationReasons.find(r => r.ID === params?.value)?.NAME,
        renderCell: (params) => { return <p style={{whiteSpace:'pre-wrap', textAlign: 'center'}}>{params?.value}</p> }
    },
    { field: 'details', headerName: '', headerAlign: "center", sortable: false, align: "center", minWidth: 50, maxWidth: 50,
      renderCell: (params) => {
        return <IconButton onClick={() => dispatch(slice.actions.setRowDetails(params?.row))}>
          <SearchIcon />
        </IconButton>
      }
    }
  ];

  return (
    <>
      <ManagePaymentDialog
        isOpen={sliceState.isManagePaymentDialogOpen}
        onClose={() => dispatch(slice.actions.setIsManagePaymentDialogOpen(false))}
        currentPaymentMethod={sliceState.selectedRow.paymentMethodInput.paymentMethod}
        handlePaymentMethodClicked={handlePaymentMethodClicked}
      />

      <PayInPersonProcedureDialog
        isOpen={sliceState.isPayInPersonDialogOpen}
        onClose={() => dispatch(slice.actions.setIsPayInPersonDialogOpen(false))}
        officeCentre={getAdminOfficeCentre(appState)}
        officeUrl={getAdminOfficeUrl(appState)}
        paymentCounterUrl={getPaymentCounterUrl(appState)}
      />

      <CCPProcedureDialog
        isOpen={sliceState.isCCPDialogOpen}
        onClose={() => {
          dispatch(slice.actions.setIsCCPDialogOpen(false));
          dispatch(slice.actions.setCCPUrl(null));
        }}
        ccpUrl={sliceState.selectedRow.ccpUrl}
        office={getAdminOffice(appState)}
      />

      <PhotoUploadDialog
        isOpen={!!sliceState.selectedRow.photoInput.photoBuf}
        onClose={() => dispatch(slice.actions.resetPhotoInput())}
        photoInput={sliceState.selectedRow.photoInput}
        onUpload={handleUpdatePhotoClicked}
      />

      <Box mb={2}>
        <div style={{display: 'flex'}}>
          <Button
            endIcon={<Refresh />}
            onClick={() => dispatch(searchHistory())}
            style={{marginLeft: "auto", fontSize: 15, fontWeight: 550}}
          >
            Refresh
          </Button>
        </div>

        {props.width > props.breakpoint
        ? <DataGrid
          columns={columnForDataGrid}
          rows={sliceState.searchResult.rows}
          getRowId={row => row.applicationId}
          rowCount={sliceState.searchResult.totalCount}
          loading={sliceState.isLoading}
          page={sliceState.searchInput.pageNum}
          onPageChange={(newPageNum) => {
            dispatch(dispatchSearchInput([{ name: 'pageNum', value: newPageNum }]))
          }}
          pageSize={sliceState.searchInput.pageSize}
          onPageSizeChange={(newPageSize) => {
            dispatch(dispatchSearchInput([{ name: 'pageSize', value: newPageSize }]))
          }}
          rowsPerPageOptions={[10, 50, 100]}
          pagination
          paginationMode='server'
          autoHeight
          getRowHeight={() => 'auto'}
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
        />
        : <>
          <ApplicationDetailsDialog
            isOpen={sliceState.selectedRow.details != null}
            onClose={() => dispatch(slice.actions.setRowDetails(null))}
            rowDetails={sliceState.selectedRow.details}
            handleManagePaymentClicked={handleManagePaymentClicked}
            PhotoPreviewButton={(row) => PhotoPreviewButton(row, dispatch)}
          />

          <DataGrid
            columns={columnForDataGridVertical}
            rows={sliceState.searchResult.rows}
            getRowId={row => row.applicationId}
            rowCount={sliceState.searchResult.totalCount}
            loading={sliceState.isLoading}
            page={sliceState.searchInput.pageNum}
            onPageChange={(newPageNum) => {
              dispatch(dispatchSearchInput([{ name: 'pageNum', value: newPageNum }]))
            }}
            pageSize={sliceState.searchInput.pageSize}
            onPageSizeChange={(newPageSize) => {
              dispatch(dispatchSearchInput([{ name: 'pageSize', value: newPageSize }]))
            }}
            rowsPerPageOptions={[10, 50, 100]}
            pagination
            paginationMode='server'
            autoHeight
            getRowHeight={() => 'auto'}
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
          />
        </>}
      </Box>
    </>
  )
}

export default UserHistory;